import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    clientSecret: String,
    successUrl: String,
    publishableKey: String,
    shippingUrl: String,
    cartId: Number,
    email: String,
    deliveryCountry: String
  }

  static targets = ["linkAuth", "addressElement", "paymentElement", "submitButton", "errors", "overlay"]

  connect() {
    this.initializeElements()
  }

  initializeElements() {
    this.stripe = Stripe(this.publishableKeyValue)
    // 16px on mobile (anything smaller makes iOS Safari zoom the field on focus),
    // 14.5px on desktop where that zoom never happens.
    //
    // 768px, matching the breakpoint the rest of the tunnel uses for exactly this rule
    // (cosy_fantasy.css, cosy_fantasy_lp.css). At the old 640 an iPhone held sideways —
    // 667px to 932px wide — counted as desktop and got 14.5px, so the card fields zoomed
    // the viewport on focus while every other input on the page did not.
    const isMobile = window.matchMedia && window.matchMedia("(max-width: 768px)").matches
    const inputSize = isMobile ? "16px" : "14.5px"

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: "flat",
        variables: {
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSizeBase: inputSize,
          fontSizeSm: "12px",
          colorPrimary: "#8C5A4A",
          colorText: "#2B2118",
          colorTextSecondary: "#7A6A58",
          colorTextPlaceholder: "#B5A492",
          colorBackground: "#FFFFFF",
          borderRadius: "8px",
          spacingUnit: "4px",
          spacingGridRow: "14px",
          spacingGridColumn: "14px"
        },
        rules: {
          ".Input": {
            border: "1.5px solid #E3D9CC",
            padding: "11px 14px",
            fontSize: inputSize,
            transition: "border-color 0.2s"
          },
          ".Input:focus": {
            border: "1.5px solid #2B2118",
            boxShadow: "none"
          },
          ".Label": {
            fontSize: "12px",
            fontWeight: "500",
            color: "#7A6A58"
          }
        }
      },
      locale: "fr"
    })

    const linkAuthElement = this.elements.create("linkAuthentication", {
      defaultValues: { email: this.emailValue || "" }
    })
    linkAuthElement.mount(this.linkAuthTarget)

    const country = this.deliveryCountryValue || "FR"
    const addressElement = this.elements.create("address", {
      mode: "shipping",
      // Stripe's own address typeahead — free, and needs no Google Places key.
      autocomplete: { mode: "automatic" },
      // The brief requires a phone number on every Cosy Fantasy order.
      fields: { phone: "always" },
      validation: { phone: { required: "always" } },
      display: { name: "split" },
      defaultValues: { address: { country: country } },
      allowedCountries: [country]
    })
    addressElement.mount(this.addressElementTarget)

    const paymentElement = this.elements.create("payment", {
      terms: { card: "never" },
      fields: { billingDetails: { address: "if_required" } }
    })
    paymentElement.mount(this.paymentElementTarget)

    // Clear the error the moment the customer edits anything. Without this the message only
    // went away on the next submit, so someone told "Votre adresse e-mail est incomplète",
    // who then fixed the address, kept reading a complaint about a field that was already
    // correct — reported with a screenshot of a complete address under that very message.
    // Every element gets it because the message names whichever field failed first, and the
    // one being corrected is not necessarily the one the message is about.
    const clearOnEdit = () => {
      if (this.errorsTarget.textContent !== "") this.errorsTarget.textContent = ""
    }
    linkAuthElement.on("change", clearOnEdit)
    addressElement.on("change", clearOnEdit)
    paymentElement.on("change", clearOnEdit)
  }

  async submit(event) {
    event.preventDefault()
    this.errorsTarget.textContent = ""
    this.setSubmitButtonsDisabled(true)

    // Any rejection (Stripe.js, address getValue, the address fetch, confirmPayment) must
    // re-enable the button — otherwise the customer is left with a dead button and can only
    // retry by reloading the page.
    try {
      const submitResult = await this.elements.submit()
      if (submitResult.error) {
        this.showError(submitResult.error.message)
        return
      }

      // Persist the shipping address on the cart BEFORE confirming: the order reads it back
      // as a fallback when the PaymentIntent carries no shipping.
      const addressElement = this.elements.getElement("address")
      const addressResult = await addressElement.getValue()
      if (addressResult.complete) {
        await this.saveShippingAddress(addressResult.value)
      }

      this.showOverlay()
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: { return_url: this.successUrlValue }
      })

      // We only reach here if confirmPayment did NOT redirect, i.e. it errored.
      if (result.error) {
        this.hideOverlay()
        this.showError(result.error.message)
      }
    } catch (err) {
      this.hideOverlay()
      this.showError("Une erreur est survenue. Veuillez réessayer.")
    }
  }

  async saveShippingAddress(addressValue) {
    const meta = document.querySelector("meta[name='csrf-token']")
    const csrfToken = meta && meta.content

    const response = await fetch(this.shippingUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        name: addressValue.name,
        address: addressValue.address,
        mobile_phone: addressValue.phone
      })
    })

    // A 4xx/5xx fetch resolves rather than rejecting, so check explicitly — otherwise we
    // would confirm the payment with the address never saved to the cart.
    if (!response.ok) throw new Error("Failed to save shipping address")
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("visible")
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("visible")
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.setSubmitButtonsDisabled(false)
  }

  setSubmitButtonsDisabled(disabled) {
    this.submitButtonTargets.forEach((button) => {
      button.disabled = disabled
    })
  }
}
